import React, { FC } from 'react';
import { TableRow, TableCell } from '@material-ui/core';
import { S3_ASSETS_PATH } from '../../../../../constants';
import { convertTimeWithTimezone } from '../../../../../utils/time';
import { ExtVettingRecord, VettingAppeal } from '../../types';
import { isValidForAppeal } from '../../utils';
import MoreButton from './MoreButton';
import ScoreTooltip from './ScoreTooltip';
import AuthplusPendingTooltip from './AuthplusPendingTooltip';
import { VettingRecordAction } from './constants';
import { usePermission } from 'portal-commons';
import { capitalize } from 'lodash-es';

interface VettingDetailsRowProps {
  record: ExtVettingRecord;
  appeals: VettingAppeal[];
  onActionClick: (
    action: VettingRecordAction,
    data: ExtVettingRecord
  ) => Promise<void> | void;
  actionGuard?: (func: (...args: any[]) => any) => any;
}

const VettingDetailsRow: FC<VettingDetailsRowProps> = ({
  record: item,
  appeals,
  onActionClick,
  actionGuard = (func: (...args: any[]) => any) =>
    (...args: any[]) =>
      func(...args),
}) => {
  const { hasPermission } = usePermission();
  const lowerCase = (vettingStatus: string) => {
    return vettingStatus.toLowerCase();
  };

  const displayVettingStatusColumn = (vettingStatus: string) => {
    return `${capitalize(vettingStatus)} ${
      lowerCase(vettingStatus) === 'unscore' ? ' (Under Review)' : ''
    }`;
  };

  const displayVettingOutcomeColumn = (
    vettingStatus: string,
    score: number | null,
    evpUid: string
  ) => {
    const lowerStatus = lowerCase(vettingStatus);
    switch (lowerStatus) {
      case 'active':
        if (score !== null) {
          return `${score}`;
        }
        return evpUid === 'CV' ? 'Verified' : '--';
      case 'pending':
        return (
          <>
            <img src={`${S3_ASSETS_PATH}/images/vetting-pending.svg`} alt="" />
            Pending
          </>
        );
      case 'expired':
        return 'Expired';
      case 'contact_vendor':
        return 'Contact Vendor';
      case 'unscore':
        return 'Unscore';
      case 'failed':
        return 'Failed';
      default:
        return '--';
    }
  };

  const handleActionClick = (action: VettingRecordAction) => {
    onActionClick?.(action, item);
  };

  return (
    <TableRow className="contentRow">
      <TableCell
        className="gridItem"
        data-testid="brandVettingDetailsBlockContainerVettingClass"
      >
        {item.vettingClass}
      </TableCell>
      <TableCell
        className="gridItem"
        data-testid="brandVettingDetailsBlockContainerEvpUid"
      >
        {capitalize(item.evpUid)}
      </TableCell>
      <TableCell
        className="gridItem vettingStatus"
        data-testid="brandVettingDetailsBlockContainerStatus"
      >
        <div className="inline-flex">
          {displayVettingStatusColumn(item.vettingStatus)}
          {item.vettingClass === 'AUTHPLUS' &&
            item.vettingStatus === 'PENDING' && (
              <AuthplusPendingTooltip data={item.authenticationPlusStatus} />
            )}
        </div>
      </TableCell>
      <TableCell
        className="gridItem date"
        data-testid="brandVettingDetailsBlockContainerDate"
      >
        {item.createDate ? convertTimeWithTimezone(item.createDate) : '--'}
      </TableCell>
      <TableCell
        className="gridItem date"
        data-testid="brandVettingDetailsBlockContainerValidUntil"
      >
        {item.validUntil ? convertTimeWithTimezone(item.validUntil) : 'N/A'}
      </TableCell>
      <TableCell
        className="gridItem outcome"
        data-testid="brandVettingDetailsBlockContainerOutcome"
      >
        <div className="inline-flex">
          {displayVettingOutcomeColumn(
            item.vettingStatus,
            item.score,
            item.evpUid
          )}
          <ScoreTooltip data={item} />
        </div>
      </TableCell>
      <TableCell
        className="gridItem action"
        data-testid="brandVettingDetailsBlockContainerAction"
      >
        {item.vettingClass === 'ENHANCED' && (
          <MoreButton
            data={item}
            menuClass="tcr-menu"
            data-testid="brandVettingDetailsBlockEnhancedMoreIconButton"
            items={[
              {
                label: 'View Report',
                onClick: () =>
                  handleActionClick(VettingRecordAction.ViewReport),
                disabled: item.vettingStatus.toLowerCase() === 'pending',
                'data-testid':
                  'brandVettingDetailsBlockEnhancedMoreMenuItemViewReport',
              },
              {
                label: 'Download Report',
                disabled: item.vettingStatus.toLowerCase() === 'pending',
                onClick: () =>
                  handleActionClick(VettingRecordAction.DownloadReport),
                'data-testid':
                  'brandVettingDetailsBlockEnhancedMoreMenuItemDownloadReport',
              },
            ]}
          />
        )}
        {item.vettingClass !== 'RCS' &&
          item.vettingClass !== 'AUTHPLUS' &&
          item.vettingClass !== 'ENHANCED' && (
            <MoreButton
              menuClass="tcr-menu"
              data={item}
              data-testid="brandVettingDetailsBlockNonEnhancedMoreIconButton"
              items={[
                {
                  label: 'Appeal',
                  onClick: actionGuard(() =>
                    handleActionClick(VettingRecordAction.Appeal)
                  ),
                  disabled: !(
                    isValidForAppeal(item) &&
                    hasPermission('brandDetail.appealVetting')
                  ),
                  'data-testid':
                    'brandVettingDetailsBlockNonEnhancedMoreMenuItemAppeal',
                },
                {
                  label: 'View Appeal History',
                  onClick: () =>
                    handleActionClick(VettingRecordAction.ViewAppealHistory),
                  disabled: appeals.every(
                    (appeal) => appeal.vettingId !== item.vettingId
                  ),
                  'data-testid':
                    'brandVettingDetailsBlockNonEnhancedMoreMenuItemViewAppealHistory',
                  hidden: item.evpUid !== 'AEGIS',
                },
              ]}
            />
          )}
        {item.vettingClass === 'RCS' && (
          <MoreButton
            menuClass="tcr-menu"
            data={item}
            data-testid="brandVettingDetailsBlockNonEnhancedMoreIconButton"
            items={[
              {
                label: 'Resend 2FA',
                onClick: () => handleActionClick(VettingRecordAction.Resend2FA),
                disabled: !hasPermission('brandDetail.send2faEmail'),
                hidden: item.vettingStatus !== 'PENDING',
                'data-testid':
                  'brandVettingDetailsBlockNonEnhancedMoreMenuItemResend2FA',
              },
              {
                label: 'Appeal',
                onClick: actionGuard(() =>
                  handleActionClick(VettingRecordAction.Appeal)
                ),
                disabled: !hasPermission('brandDetail.appealVetting'),
                hidden:
                  item.vettingStatus !== 'ACTIVE' &&
                  item.vettingStatus !== 'FAILED',
                'data-testid':
                  'brandVettingDetailsBlockNonEnhancedMoreMenuItemAppeal',
              },
              {
                label: 'Show Appeal History',
                onClick: () =>
                  handleActionClick(VettingRecordAction.ViewAppealHistory),
                disabled: appeals.every(
                  (appeal) => appeal.vettingId !== item.vettingId
                ),
                hidden: item.vettingStatus === 'PENDING',
                'data-testid':
                  'brandVettingDetailsBlockNonEnhancedMoreMenuItemViewAppealHistory',
              },
            ]}
          />
        )}
        {item.vettingClass === 'AUTHPLUS' && (
          <MoreButton
            data-testid="brandVettingDetailsBlockAuthplusMoreIconButton"
            menuClass="tcr-menu"
            data={item}
            items={[
              {
                'data-testid':
                  'brandVettingDetailsBlockAuthplusMoreMenuItemResend2FA',
                label: 'Resend 2FA',
                onClick: () => handleActionClick(VettingRecordAction.Resend2FA),
                disabled: !hasPermission('brandDetail.send2faEmail'),
                hidden: item.vettingStatus !== 'PENDING',
              },
              {
                'data-testid':
                  'brandVettingDetailsBlockAuthplusMoreMenuItemAppeal',
                label: 'Appeal',
                onClick: actionGuard(() =>
                  handleActionClick(VettingRecordAction.Appeal)
                ),
                disabled: !hasPermission('brandDetail.appealVetting'),
                hidden: item.vettingStatus !== 'FAILED',
              },
              {
                'data-testid':
                  'brandVettingDetailsBlockAuthplusMoreMenuItemViewAppealHistory',
                label: 'Show Appeal History',
                onClick: () =>
                  handleActionClick(VettingRecordAction.ViewAppealHistory),
                disabled: appeals.every(
                  (appeal) => appeal.vettingId !== item.vettingId
                ),
                hidden: item.vettingStatus === 'PENDING',
              },
            ]}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

export default VettingDetailsRow;
